/* MV119 (perf v7) — mavely-perf.v1 with ONE change: how it notices the
   dashboard. v1 re-read document.body.textContent — the text of the whole
   page — on every DOM change, for as long as no dashboard appeared, which
   for a visitor is forever: a full-page string build per React render,
   per deals-list page, per badge. Now the check looks only at the page's
   headings, and the watcher runs at most every 400 ms. The four signals,
   the admin files it loads and the lazy-image half are unchanged. v1 is
   still on disk.
   Also: the lazy-image half reads each image's position from an
   IntersectionObserver instead of getBoundingClientRect() (a forced
   layout of the whole page per pass). Same test, same result. */
/* ===========================================================================
   MAVELY PERF v1
   ---------------------------------------------------------------------------
   Two jobs, both aimed at what the Lighthouse report actually complained about:

     1. KEEP THE ADMIN-ONLY SCRIPTS OFF PUBLIC PAGES.
        mavely-admin2 and mavely-adminplus exist to add controls to the Link
        Ledger dashboard. Every visitor who has never seen your dashboard was
        downloading, parsing and executing them anyway. That is most of
        "Reduce unused JavaScript" and a real slice of "Reduce JavaScript
        execution time" and "Minimize main-thread work".

        They are now fetched only when there is a dashboard to attach them to.

     2. LAZY-LOAD IMAGES THAT ARE NOT ON SCREEN YET.
        Deal cards can run to dozens of product photos. Loading them all up
        front is what pushes Speed Index up and drags "enormous network
        payloads" along with it. Anything below the fold now waits.

   ---------------------------------------------------------------------------
   THE RULE THIS FILE FOLLOWS
   ---------------------------------------------------------------------------
   IT MUST BE IMPOSSIBLE FOR THIS TO COST YOU A FEATURE.

   Deferring a script is only safe if the trigger to load it is never missed.
   So the admin scripts load on ANY of four independent signals, and the last
   of them is a permanent DOM watcher — if the first three are all somehow
   wrong, the dashboard appearing on screen still pulls them in. Being early
   costs a download that would have happened anyway. Being late costs a broken
   dashboard, so the checks are deliberately generous.
   =========================================================================== */

(function () {
    'use strict';

    if (window.__MAVELY_PERF_V1__) return;
    window.__MAVELY_PERF_V1__ = true;

    /* =================================================================
       PART 1 — ADMIN SCRIPTS, LOADED ONLY WHEN THERE IS AN ADMIN
       ================================================================= */

    /* Keep this list in the same order the tags were in, and load them in
       that order — mavely-admin2 registers panels that adminplus expects. */
    var ADMIN_ASSETS = [
        { css: '/assets/mavely-admin2.v3.min.css',    js: '/assets/mavely-admin2.v3.min.js' },
        { css: '/assets/mavely-adminplus.v1.min.css', js: '/assets/mavely-adminplus.v1.min.js' }
    ];

    var loaded = false;

    function loadAdmin(reason) {
        if (loaded) return;
        loaded = true;

        ADMIN_ASSETS.forEach(function (a) {
            if (a.css && !document.querySelector('link[href="' + a.css + '"]')) {
                var l = document.createElement('link');
                l.rel = 'stylesheet';
                l.href = a.css;
                document.head.appendChild(l);
            }
        });

        /* Sequential, not parallel: these run in order today and that order is
           part of why they work. Speed is not the point once you are already
           signed in to a dashboard. */
        (function next(i) {
            if (i >= ADMIN_ASSETS.length) return;
            var src = ADMIN_ASSETS[i].js;
            if (!src || document.querySelector('script[src="' + src + '"]')) { next(i + 1); return; }
            var s = document.createElement('script');
            s.src = src;
            s.onload  = function () { next(i + 1); };
            s.onerror = function () { next(i + 1); };   // one failure must not stall the rest
            document.head.appendChild(s);
        })(0);

        if (window.console && console.info) {
            console.info('[mavely-perf] admin scripts loaded (' + reason + ')');
        }
    }

    /* --- signal 1: the URL is an admin route ------------------------------ */
    function pathLooksAdmin() {
        var p = (location.pathname || '').toLowerCase();
        return p.indexOf('/admin') === 0
            || p.indexOf('/dashboard') === 0
            || p.indexOf('landing-admin') !== -1
            || (location.hash || '').toLowerCase().indexOf('admin') !== -1;
    }

    /* --- signal 2: this browser has signed in before ---------------------- */
    /* Generous on purpose. A stale token that no longer works still means the
       person uses the dashboard from this browser, and loading two files they
       will probably want shortly is a far smaller cost than not having them. */
    function hasAdminToken() {
        try { return !!localStorage.getItem('sh_admin_token'); } catch (e) { return false; }
    }

    /* --- signal 3: the dashboard is on screen right now ------------------- */
    function dashboardVisible() {
        /* MV119: the heading, not the whole page text */
        try {
            var hs = document.querySelectorAll('h1, h2, h3');
            for (var i = 0; i < hs.length; i++) {
                if (String(hs[i].textContent || '').indexOf('Link Ledger Dashboard') !== -1) return true;
            }
        } catch (e) {}
        return false;
    }

    if (pathLooksAdmin() || hasAdminToken()) {
        loadAdmin('route or saved sign-in');
    } else {
        /* --- signal 4: the safety net ------------------------------------ */
        /* This is a single-page app: signing in swaps the dashboard into the
           page without a navigation, so there is no reload to hook. Watching
           the DOM is the only reliable way to notice. It disconnects the
           moment it fires, so it costs nothing after that. */
        var startWatch = function () {
            if (!document.body) return;
            if (dashboardVisible()) { loadAdmin('dashboard rendered'); return; }

            var moPending = false;                       // MV119: at most every 400 ms
            var mo = new MutationObserver(function () {
                if (moPending || loaded) return;
                moPending = true;
                setTimeout(function () {
                    moPending = false;
                    if (loaded) { mo.disconnect(); return; }
                    if (hasAdminToken() || dashboardVisible()) {
                        mo.disconnect();
                        loadAdmin('dashboard appeared');
                    }
                }, 400);
            });
            mo.observe(document.body, { childList: true, subtree: true });

            /* A sign-in writes the token before the dashboard finishes drawing,
               so poll briefly as well. Cheap, and stops after two minutes. */
            var ticks = 0;
            var iv = setInterval(function () {
                if (loaded || ++ticks > 240) { clearInterval(iv); return; }
                if (hasAdminToken()) { clearInterval(iv); loadAdmin('signed in'); }
            }, 500);
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', startWatch);
        } else {
            startWatch();
        }
    }

    /* Manual override, for the console or for anything that wants certainty. */
    window.__mvLoadAdmin = function () { loadAdmin('called manually'); return true; };

    /* =================================================================
       PART 2 — LAZY IMAGES
       =================================================================
       Only ever ADDS the hints, and only to images that have not already
       said something about themselves. An image already marked eager, or
       already carrying loading=, is left exactly as its author wrote it.
       ================================================================= */

    var SKIP_FIRST = 3;   // the top few are likely the LCP element — never defer those
    var firstPass  = true;

    var lazyIO = null;                                   // MV119
    try {
        if ('IntersectionObserver' in window) {
            lazyIO = new IntersectionObserver(function (entries) {
                for (var k = 0; k < entries.length; k++) {
                    var t = entries[k].target;
                    lazyIO.unobserve(t);
                    if (t.hasAttribute('loading')) continue;
                    var r = entries[k].boundingClientRect, rb = entries[k].rootBounds;
                    var vh = (rb && rb.height) || window.innerHeight || 800;
                    if (!r || r.top > vh) t.setAttribute('loading', 'lazy');
                }
            });
        }
    } catch (e) { lazyIO = null; }

    function lazify() {
        var imgs;
        try { imgs = document.querySelectorAll('img:not([data-mvperf])'); }
        catch (e) { return; }

        for (var i = 0; i < imgs.length; i++) {
            var im = imgs[i];
            im.setAttribute('data-mvperf', '1');

            /* decoding=async is applied to everything, hero included. It only
               tells the browser it may decode off the main thread — it never
               delays a request, so there is no reason to hold it back. Keeping
               this above the skip below matters: an earlier version had the
               hero exit before reaching this line, which quietly dropped the
               hint from the three images most worth having it on. */
            if (!im.hasAttribute('decoding')) im.setAttribute('decoding', 'async');

            /* Author already decided. Respect it. */
            if (im.hasAttribute('loading')) continue;

            /* Deferring the hero would make LCP worse, not better, which is
               the classic way this optimisation backfires. Only the very first
               pass can contain the hero; everything found later arrived after
               the fold was painted. */
            if (firstPass && i < SKIP_FIRST) continue;

            /* MV119: where the image sits is read by an IntersectionObserver,
               after the browser's own layout, instead of by
               getBoundingClientRect() here — which forced a layout of the
               whole page in the middle of the frame, the biggest single
               "forced reflow" in the report. Same test, same attribute,
               one frame later. No IntersectionObserver: the old way. */
            if (lazyIO) { lazyIO.observe(im); continue; }

            var r = null;
            try { r = im.getBoundingClientRect(); } catch (e) {}
            var below = !r || r.top > (window.innerHeight || 800);

            if (below) im.setAttribute('loading', 'lazy');
        }

        firstPass = false;
    }

    var pending = false;
    function scheduleLazify() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
            pending = false;
            try { lazify(); } catch (e) {}
        });
    }

    function startLazy() {
        if (!document.body) return;
        lazify();
        new MutationObserver(function (recs) {
            for (var i = 0; i < recs.length; i++) {
                if (recs[i].addedNodes && recs[i].addedNodes.length) { scheduleLazify(); return; }
            }
        }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startLazy);
    } else {
        startLazy();
    }
})();
